import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import { SAISIE } from "@/lib/roles";
import { ShieldCheck, AlertTriangle, Egg, Truck } from "lucide-react";

export default function PublicTrace() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(undefined); // undefined loading, null not found

  useEffect(() => {
    api.get(`/public/palette/${code}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [code]);

  if (data === undefined)
    return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-400">Chargement…</div>;

  if (data === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-slate-400 mb-3" />
          <div className="font-heading font-black text-2xl uppercase text-slate-700">Palette introuvable</div>
          <div className="text-slate-500 mt-1">Code {code}</div>
        </div>
      </div>
    );

  const isDec = data.type === "DECLASSEE";

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-center gap-2 mb-5">
          <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
            <Egg className="w-5 h-5 text-amber-400" />
          </div>
          <span className="font-heading font-black text-xl uppercase text-slate-900">Ferme d'Autheuil</span>
        </div>

        {/* Status banner */}
        <div
          data-testid="public-qr-safety-banner"
          className={`rounded-t-2xl p-5 text-center text-white ${isDec ? "bg-rose-600" : "bg-emerald-600"}`}
        >
          {isDec ? <AlertTriangle className="w-10 h-10 mx-auto mb-2" /> : <ShieldCheck className="w-10 h-10 mx-auto mb-2" />}
          <div className="font-heading font-black text-2xl uppercase tracking-tight">
            {isDec ? "Avertissement majeur" : "Conforme — Catégorie 1"}
          </div>
          {isDec && <div className="font-bold text-sm mt-1 uppercase tracking-wide">Non destiné à la consommation directe</div>}
        </div>

        <div className="bg-white rounded-b-2xl border-2 border-t-0 border-slate-200 p-6 space-y-3">
          <div className="text-center border-b-2 border-slate-100 pb-3">
            <div className="text-xs uppercase tracking-wide text-slate-400 font-bold">N° Palette</div>
            <div className="font-mono font-black text-2xl text-slate-900">{data.code_qr}</div>
          </div>

          <Field label="Exploitation" value={data.exploitation} />
          <Field label="Bâtiment" value={`${data.batiment} — ${data.batiment_code}`} />
          <Field label="Date de ponte" value={fmtDate(data.date_ponte)} />
          {isDec ? (
            <Field label="Motif de déclassement" value={data.motif} accent />
          ) : (
            <Field label="Catégorie" value={data.categorie} />
          )}
          <Field label="Poids net officiel" value={`${data.poids_net.toFixed(1)} kg`} />
          <Field
            label="Statut d'expédition"
            value={
              data.statut === "expediee" && data.bl
                ? `Expédiée le ${fmtDate(data.bl.date_depart)} vers ${data.bl.destinataire} (${data.bl.numero})`
                : "En stock à l'exploitation"
            }
          />
        </div>

        {user && SAISIE.includes(user.role) && data.statut === "en_stock" && (
          <button
            data-testid="public-add-to-bl-btn"
            onClick={() => navigate(`/bl/nouveau?code=${data.code_qr}`)}
            className="mt-4 w-full min-h-[64px] rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-heading font-black text-lg uppercase tracking-wide flex items-center justify-center gap-2"
          >
            <Truck className="w-6 h-6" /> Ajouter à un bon de livraison
          </button>
        )}

        <div className="text-center text-xs text-slate-400 mt-4 font-semibold">
          Fiche de traçabilité publique • Ferme d'Autheuil
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, accent }) {
  return (
    <div className="flex justify-between items-baseline gap-3 border-b border-dashed border-slate-200 pb-2">
      <span className="text-xs uppercase tracking-wide text-slate-400 font-bold shrink-0">{label}</span>
      <span className={`font-bold text-right ${accent ? "text-rose-700" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}
